<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '../_base-button.vue'
import BaseIcon from '../_base-icon.vue'
import paywallTracker from '@services/mixpanel/paywall-tracker'
import { CTA_TYPES, DISMISS_METHODS } from '@services/mixpanel/analytics-spec'

const props = defineProps({
    data: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['close'])

const router = useRouter()

const headline = computed(() => props.data.headline)

const subDescription = computed(() => props.data.subDescription)

const primaryAction = computed(() => props.data.primaryAction)

const secondaryAction = computed(() => props.data.secondaryAction)

function getCTAType(action) {
    if (!action) return CTA_TYPES.UPGRADE
    const text = action.text?.toLowerCase() || ''
    if (text.includes('trial')) return CTA_TYPES.START_TRIAL
    if (text.includes('restore')) return CTA_TYPES.RESTORE
    if (text.includes('plan')) return CTA_TYPES.VIEW_PLANS
    return CTA_TYPES.UPGRADE
}

function close(dismissMethod = DISMISS_METHODS.CLOSE) {
    paywallTracker.onWallDismissed(dismissMethod)
    emit('close')
}

function closeWithoutTracking(result) {
    emit('close', result)
}

function handlePrimaryAction() {
    paywallTracker.onCtaTapped(getCTAType(primaryAction.value))
    if (primaryAction.value.route) {
        router.push(primaryAction.value.route)
    }
    closeWithoutTracking('primary')
}

function handleSecondaryAction() {
    if (!secondaryAction.value) return

    paywallTracker.onCtaTapped(getCTAType(secondaryAction.value))
    if (secondaryAction.value.route) {
        router.push(secondaryAction.value.route)
    }
    closeWithoutTracking('secondary')
}
</script>

<template>
    <div class="premium-upsell-modal">
        <button
            class="close-button"
            aria-label="Close modal"
            @click="close()"
        >
            <BaseIcon
                name="close"
                :size="20"
            />
        </button>

        <div class="content">
            <BaseIcon
                class="premium-logo"
                name="logo-premium-mark"
                :size="32"
            />

            <h2 class="headline">{{ headline }}</h2>

            <p
                v-if="subDescription"
                class="sub-description"
            >{{ subDescription }}</p>

            <div class="actions">
                <BaseButton
                    variant="primary"
                    size="l"
                    @click="handlePrimaryAction"
                >
                    {{ primaryAction.text }}
                </BaseButton>

                <button
                    v-if="secondaryAction"
                    class="secondary-link"
                    @click="handleSecondaryAction"
                >
                    {{ secondaryAction.text }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use '@design' as *;

.premium-upsell-modal {
    position: relative;
    width: 90vw;
    max-width: 710px;
    padding: $s-h $s-h $s-xxxl;
    border: none;
    border-radius: $radius-12;

    @include elevationDialog;

    @include mobileOnly {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: calc(100vw - $s-xxl);
        max-width: none;
        height: auto;
        padding: $s-xxl 30px 36px;
        margin: $s-l;
        border-radius: $radius-16;
    }
}

.close-button {
    position: absolute;
    top: $s-l;
    right: $s-l;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: $s-xxs;
    color: $c-modal-close;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;

    @include transition((color, background-color), fast);

    svg {
        width: $s-l;
        height: $s-l;
    }

    &:hover {
        color: $c-modal-close-hover;
        background: $c-modal-close-hover-bg;
    }

    @include mobileOnly {
        top: $s-l;
        right: $s-l;
    }
}

.content {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    text-align: left;
}

.premium-logo {
    display: block;
    margin-bottom: 32px;
    color: $c-accent-default;

    @include mobileOnly {
        margin-bottom: $s-xl;
    }
}

.headline {
    margin: 0;
    margin-bottom: $s-xl;
    font-family: $system-default-font-family;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        margin-bottom: $s-l;
        font-size: 27px;
        line-height: 1.15;
    }
}

.sub-description {
    margin: 0;
    margin-top: -$s-m;
    margin-bottom: 140px;
    font-family: $system-default-font-family;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        margin-top: -$s-s;
        margin-bottom: 80px;
        font-size: 27px;
        line-height: 1.15;
    }
}

// The primary button is BaseButton's own L pill, hugging its label (T8) — no local
// geometry. The stack's gap separates the CTA from the secondary link.
.actions {
    display: flex;
    flex-direction: column;
    gap: $space-24;
    align-items: center;
    width: 100%;
    margin-top: $s-xs;

    @include mobileOnly {
        flex-direction: row;
        flex-wrap: wrap;
        gap: $s-m;
        align-items: center;
        justify-content: center;
        margin-top: $s-xl;
    }
}

.secondary-link {
    @extend %p;

    align-self: center;
    font-size: 15px;
    font-weight: 400;
    color: $c-text-secondary;
    text-align: center;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: none;

    @include transition((color, background-color), fast);

    @include mobileOnly {
        flex-shrink: 0;
        align-self: center;
        padding: 0 $s-xs;
        margin-top: 0;
        font-size: 14px;
        white-space: nowrap;
    }

    &:hover {
        font-weight: 700;
        color: $c-text-primary;
    }
}
</style>
